/**
 * YuNet face detector adapter (the port of cv2.FaceDetectorYN), backed by
 * onnxruntime-web running models/yunet.onnx.
 *
 * YuNet-2023 is anchor-free / point-based with strides {8,16,32}. For each
 * stride s the model emits, over an (H/s)·(W/s) grid:
 *   cls_s  [1, N, 1]  class score (sigmoid already applied in this export),
 *   obj_s  [1, N, 1]  objectness score (sigmoid already applied),
 *   bbox_s [1, N, 4]  box regression (cx_off, cy_off, w_log, h_log),
 *   kps_s  [1, N, 10] (unused here).
 *
 * Decode (matching OpenCV's FaceDetectorYN postprocess):
 *   cx = (col + bbox[0]) · s ; cy = (row + bbox[1]) · s
 *   w  = exp(bbox[2]) · s     ; h  = exp(bbox[3]) · s
 *   score = sqrt(cls · obj)
 * then box (x,y,w,h) = (cx - w/2, cy - h/2, w, h), with NMS by IoU.
 *
 * FIDELITY NOTE: this is a from-scratch re-implementation of OpenCV's internal
 * decode, not a binding to it. The grid/stride/exp-decode and the
 * score=sqrt(cls·obj) rule match OpenCV's source. The one assumption is that
 * this particular ONNX export bakes the sigmoid into cls/obj (true for the
 * OpenCV-Zoo 2023mar export shipped here). The model input must be **even**
 * sized (the strides divide it); we pad to the next multiple of 32. Confidence
 * threshold and NMS IoU mirror the Python construction (0.6 / 0.3).
 */
import * as ort from 'onnxruntime-web';
import { ImagePixels } from '../../core/ports';
import { FaceBox, FaceDetector } from '../ports';
import { resizeImage } from '../core/image';

const STRIDES = [8, 16, 32];
const CONF_THRESHOLD = 0.6; // matches cv2.FaceDetectorYN.create(..., 0.6, ...)
const NMS_IOU = 0.3;
/** This ONNX export has a FIXED 640x640 input (cv2.FaceDetectorYN resized
 * dynamically; the raw model does not). We resize to it and scale boxes back. */
const MODEL_SIZE = 640;

export interface YunetOptions {
  modelUrl?: string;
  executionProviders?: ('webgpu' | 'wasm' | 'cpu')[];
  /** Where onnxruntime-web should fetch its wasm binaries (browser). */
  wasmPaths?: string;
  session?: ort.InferenceSession;
}

export class YunetFaceDetector implements FaceDetector {
  private session?: ort.InferenceSession;
  private loadPromise?: Promise<ort.InferenceSession>;
  private readonly modelUrl: string;
  private readonly eps: ('webgpu' | 'wasm' | 'cpu')[];
  private readonly wasmPaths?: string;

  constructor(opts: YunetOptions = {}) {
    this.session = opts.session;
    this.modelUrl = opts.modelUrl ?? 'yunet.onnx';
    this.eps = opts.executionProviders ?? ['wasm'];
    this.wasmPaths = opts.wasmPaths;
  }

  private load(): Promise<ort.InferenceSession> {
    if (this.session) return Promise.resolve(this.session);
    if (this.wasmPaths) {
      ort.env.wasm.wasmPaths = this.wasmPaths;
      ort.env.wasm.numThreads = 1;
    }
    this.loadPromise ??= ort.InferenceSession.create(this.modelUrl, {
      executionProviders: this.eps,
    }).then((s) => (this.session = s));
    return this.loadPromise;
  }

  async detect(img: ImagePixels): Promise<FaceBox[]> {
    const session = await this.load();
    // The model input is a fixed MODEL_SIZE square: resize to it, then scale the
    // detected boxes back to the original image coordinates.
    const S = MODEL_SIZE;
    const small = resizeImage(img, S, S);
    const sd = small.data;
    const plane = S * S;
    const inp = new Float32Array(3 * plane);
    // YuNet expects BGR, raw 0..255, NCHW.
    for (let i = 0; i < plane; i++) {
      inp[i] = sd[i * 4 + 2]; // B
      inp[plane + i] = sd[i * 4 + 1]; // G
      inp[2 * plane + i] = sd[i * 4]; // R
    }
    const out = await session.run({ input: new ort.Tensor('float32', inp, [1, 3, S, S]) });

    const sx = img.width / S;
    const sy = img.height / S;
    const boxes: FaceBox[] = [];
    for (const s of STRIDES) {
      const cls = out[`cls_${s}`]?.data as Float32Array | undefined;
      const obj = out[`obj_${s}`]?.data as Float32Array | undefined;
      const bbox = out[`bbox_${s}`]?.data as Float32Array | undefined;
      if (!cls || !obj || !bbox) continue;
      const cols = S / s;
      const rows = S / s;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const idx = r * cols + c;
          const score = Math.sqrt(Math.max(0, cls[idx]) * Math.max(0, obj[idx]));
          if (score < CONF_THRESHOLD) continue;
          const cx = (c + bbox[idx * 4]) * s;
          const cy = (r + bbox[idx * 4 + 1]) * s;
          const bw = Math.exp(bbox[idx * 4 + 2]) * s;
          const bh = Math.exp(bbox[idx * 4 + 3]) * s;
          // Scale 640-space box back to the original image.
          boxes.push({ x: (cx - bw / 2) * sx, y: (cy - bh / 2) * sy, w: bw * sx, h: bh * sy, conf: score });
        }
      }
    }
    return nms(boxes, NMS_IOU);
  }
}

function iou(a: FaceBox, b: FaceBox): number {
  const x1 = Math.max(a.x, b.x);
  const y1 = Math.max(a.y, b.y);
  const x2 = Math.min(a.x + a.w, b.x + b.w);
  const y2 = Math.min(a.y + a.h, b.y + b.h);
  const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const union = a.w * a.h + b.w * b.h - inter;
  return union > 0 ? inter / union : 0;
}

function nms(boxes: FaceBox[], thr: number): FaceBox[] {
  const sorted = boxes.slice().sort((p, q) => q.conf - p.conf);
  const keep: FaceBox[] = [];
  for (const b of sorted) {
    if (keep.every((k) => iou(k, b) < thr)) keep.push(b);
  }
  return keep;
}
